// Plan change request modal — opens from /settings/billing when a customer
// clicks "Request plan change" / "Upgrade plan".
//
// Behavior:
//  - Shows the four plans side by side (Free / Growth / Scale / Enterprise).
//  - The customer's current plan is highlighted and not selectable.
//  - Free / Growth / Scale are self-serve (Submit posts to the API).
//  - Enterprise is intentionally NOT self-serve — it routes to a
//    mailto link to sales (decision: KAN follow-up).
//  - After submit, the parent re-renders with a pending banner; this modal closes.

import { capture } from "@/lib/posthog";
import * as React from "react";
import { X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { billingTestIds } from "@/testids/billingTestIds";
import { useCreatePlanChangeRequest } from "@/hooks/use-plan-change-requests";
import { isSelfServeChange, type PlanTier } from "@/lib/plan-tier";
import { usePlans } from "@/hooks/use-plans";

const SALES_EMAIL = "sales@fastn.ai";

interface PlanOption {
  tier: PlanTier;
  name: string;
  features: string[];
}

const PLAN_OPTIONS: PlanOption[] = [
  {
    tier: "free",
    name: "Free",
    features: [
      "50 AI credits / month",
      "Diagrams and docs view",
      "Read-only workflow code",
    ],
  },
  {
    tier: "growth",
    name: "Growth",
    features: [
      "500 AI credits / month",
      "Full workflow code editor",
      "Workflow execution + history",
      "Email support",
    ],
  },
  {
    tier: "scale",
    name: "Scale",
    features: [
      "5,000 AI credits / month",
      "Custom quotas + safety brakes",
      "Priority support",
      "SSO + audit log",
    ],
  },
  {
    tier: "enterprise",
    name: "Enterprise",
    features: [
      "Unlimited AI credits",
      "Dedicated workspace DB",
      "Custom SLAs",
      "Named technical contact",
    ],
  },
];


interface Props {
  currentPlan: PlanTier;
  onClose: () => void;
}

export function PlanChangeRequestModal({ currentPlan, onClose }: Props): React.ReactElement {
  // The price comes from the API, never from a literal here. This modal used to
  // carry its own table and told everyone Growth was $50/mo while the API — and
  // the billing page beside it — said "$15 per account, per month".
  const { data: plans = [] } = usePlans();
  // Default selection = the next paid tier above current. For enterprise (no
  // higher tier) we just pick the current as a no-op placeholder.
  const defaultSelection: PlanTier =
    currentPlan === "free" ? "growth"
    : currentPlan === "growth" ? "scale"
    : currentPlan === "scale" ? "enterprise"
    : "enterprise";
  const [selected, setSelected] = React.useState<PlanTier>(defaultSelection);

  // Reveal the address once someone has clicked a Contact sales link.
  //
  // A `mailto:` produces NO visible change when the browser has no handler
  // registered — no tab, no dialog, no error. That is the default on a managed
  // corporate laptop and in Chrome until a handler is set, and it is what
  // PostHog caught: 43 rage clicks on /settings/billing across 4 sessions and 4
  // people, ~11 per session, resolving to the element text "Contact sales".
  // Many clicks from very few people is a broken control, not a confusing page.
  //
  // The mailto stays — routing enterprise to sales is deliberate. What was
  // missing is any evidence to the user that the click did something, so the
  // address appears beside it and can be copied by hand.
  const [salesRevealed, setSalesRevealed] = React.useState(false);
  const [salesCopied, setSalesCopied] = React.useState(false);
  const onContactSales = (): void => {
    setSalesRevealed(true);
    capture("upgrade_prompt_shown", {
      trigger: "billing_page",
      dimension: null,
      plan: currentPlan,
    });
  };
  const copySalesEmail = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(SALES_EMAIL);
      setSalesCopied(true);
      window.setTimeout(() => setSalesCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated; the address is selectable either way,
      // which is the whole point of showing it.
    }
  };

  const createReq = useCreatePlanChangeRequest();

  // ESC closes the modal.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submittable = isSelfServeChange(currentPlan, selected);
  // Everything that is not self-serve reaches sales instead. Without this the
  // footer's only control was a disabled "Request Enterprise" button — a scale
  // org opens the modal on exactly that selection, so the whole upgrade flow
  // dead-ended with nothing to click and nothing explaining why.
  const needsSales = !submittable && selected !== currentPlan;

  const handleSubmit = () => {
    if (!submittable) return;
    createReq.mutate(selected, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[1000] bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-change-modal-title"
        data-testid={billingTestIds.planChangeModal}
        className="fixed left-1/2 top-1/2 z-[1001] w-[min(900px,92vw)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-card shadow-2xl" // design-tokens-ignore: overlay exception
      >
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div>
            <h2 id="plan-change-modal-title" className="text-lg font-semibold text-foreground">
              Request plan change
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Your platform admin will review and apply the change. You're currently on the
              <span className="mx-1 font-semibold capitalize text-foreground">{currentPlan}</span>
              plan.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
            data-testid={billingTestIds.planChangeModalCloseButton}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 p-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLAN_OPTIONS.map((p) => {
            const isCurrent = p.tier === currentPlan;
            const selfServe = isSelfServeChange(currentPlan, p.tier);
            const isSelected = p.tier === selected && selfServe;
            // Contact-sales applies to anything non-self-serve and not current:
            // enterprise upgrades AND any downgrade to a lower-rank tier.
            const showContactSales = !isCurrent && !selfServe;

            return (
              <button
                key={p.tier}
                type="button"
                onClick={() => { if (selfServe) setSelected(p.tier); }}
                disabled={isCurrent}
                data-testid={billingTestIds.planChangeOption(p.tier)}
                className={cn(
                  "flex flex-col gap-3 rounded-md border p-4 text-left transition-[background-color,border-color,color,opacity,transform]",
                  isSelected
                    ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                    : "border-border bg-background hover:border-primary/40",
                  isCurrent && "opacity-60 cursor-not-allowed",
                  showContactSales && "cursor-default hover:border-border",
                )}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-base font-semibold capitalize text-foreground">{p.name}</div>
                    <div className="text-sm tabular-nums text-muted-foreground">
                      {plans.find((pl) => pl.tier === p.tier)?.priceLabel ?? "\u2014"}
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Current
                    </span>
                  )}
                </div>
                <ul className="flex flex-col gap-1.5 text-xs text-muted-foreground">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {showContactSales && (
                  <a
                    href={`mailto:${SALES_EMAIL}?subject=Fastn%20Plan%20Change%20-%20${p.name}`}
                    onClick={(e) => { e.stopPropagation(); onContactSales(); }}
                    data-testid={billingTestIds.planChangeContactSalesButton}
                    className="mt-auto inline-flex items-center justify-center rounded-md border border-primary bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20"
                  >
                    Contact sales
                  </a>
                )}
              </button>
            );
          })}
        </div>

        {createReq.isError && (
          <div className="mx-6 mb-4 rounded border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            {(createReq.error as Error)?.message ?? "Failed to submit request"}
          </div>
        )}

        {salesRevealed && (
          <div
            className="mx-6 mb-3 flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-fg-muted"
            data-testid={billingTestIds.planChangeSalesEmailFallback}
          >
            <span>
              If your mail app did not open, email{" "}
              <span className="font-mono text-fg">{SALES_EMAIL}</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-6 px-2 text-xs"
              onClick={copySalesEmail}
              data-testid={billingTestIds.planChangeSalesEmailCopyButton}
            >
              {salesCopied ? "Copied" : "Copy"}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-6 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={createReq.isPending}
          >
            Cancel
          </Button>
          {needsSales ? (
            <a
              href={`mailto:${SALES_EMAIL}?subject=Fastn%20Plan%20Change%20-%20${PLAN_OPTIONS.find((p) => p.tier === selected)?.name ?? "plan"}`}
              onClick={onContactSales}
              data-testid={billingTestIds.planChangeContactSalesFooterButton}
              className="inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Contact sales
            </a>
          ) : (
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!submittable || createReq.isPending}
              data-testid={billingTestIds.planChangeSubmitButton}
            >
              {createReq.isPending ? "Submitting..." : `Request ${PLAN_OPTIONS.find((p) => p.tier === selected)?.name ?? "plan"}`}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
